//
// README - Beta Component
//
// This component is marked "beta" and will go through some iterations
// before landing at a final solution. Please reach out to the Cadence team
// if you intend to use this in the meantime and we'll help you out!
//

import React from 'react';
import classNames from 'classnames';
import { RCStarRating } from '../';
import ExpeditedShippingTruckIcon from '../../images/nudge/fast-truck-tiny.svg?component';

interface LinkElementProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  className?: string;
  children?: React.ReactNode;
}

interface ListingProps {
  condition: string;
  isWatched?: boolean;
  originalPrice?: string;
  discountText?: string;
  price: string;
  shippingPrice?: string;
  expeditedShipping?: boolean;
  shopLocation: string;
  shopName: string;
  shopRating: number;
  signals?: {
    name: any; // TODO
    title: any; // TODO
    icon: any; // TODO
  }[];
  thumbnailAlt: string;
  thumbnailURL: string;
  title: string;
}

interface CardProps {
  a11yPriceDescription: string;
  borderless?: boolean;
  disableCardClick?: boolean;
  linkElement?: (children: React.ReactNode) => React.ReactElement<LinkElementProps>;
  onWatchClick: (any) => void;
  primaryAction?: React.ReactElement;
  secondaryAction?: React.ReactElement;
  TitleElement?: 'h3' | 'h4' | 'div';
  verticalStretch?: boolean;
  watchButtonElement?: React.ReactElement;
  loading?: boolean;
}

export type RCComparisonCardProps = ListingProps & CardProps;

export function RCComparisonCard({
  a11yPriceDescription,
  borderless = false,
  condition,
  disableCardClick = false,
  discountText = undefined,
  expeditedShipping = false,
  isWatched = false,
  linkElement = undefined,
  signals = undefined,
  onWatchClick,
  originalPrice = undefined,
  price,
  primaryAction = undefined,
  secondaryAction = undefined,
  shippingPrice = undefined,
  shopLocation,
  shopName,
  shopRating,
  thumbnailAlt,
  thumbnailURL,
  title,
  TitleElement = 'h3',
  verticalStretch = false,
  watchButtonElement = undefined,
  loading = false,
}: RCComparisonCardProps) {
  function PassedLink({ children, className, ...passthroughProps }: LinkElementProps) {
    if (!linkElement) {
      return <>{children}</>;
    }

    return React.cloneElement(
      linkElement(children),
      {
        className: className,
        ...passthroughProps,
      },
    );
  }

  function LoadingStateUI() {
    return (
      <div className="rc-comparison-card rc-comparison-card--loading">
        <div className="rc-comparison-card__thumbnail" />
        <div className="rc-comparison-card__body">
          <div className="rc-comparison-card__titleblock">
            <div className="rc-comparison-card__title" />
            <div className="rc-comparison-card__condition" />
          </div>
          <div className="rc-comparison-card__price-block">
            <div className="rc-comparison-card__price" />
            <div className="rc-comparison-card__shipping-price" />
          </div>
          <div className="rc-comparison-card__shop">
            <div className="rc-comparison-card__shop-name" />
            <div className="rc-comparison-card__shop-location" />
          </div>
          <div className="rc-comparison-card__actions">
            <div className="rc-comparison-card__action" />
            <div className="rc-comparison-card__action" />
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return <LoadingStateUI />;
  }

  return (
    <div
      className={classNames(
        'rc-comparison-card',
        {
          'rc-comparison-card--borderless': borderless,
          'rc-comparison-card--reduced-price': originalPrice,
          'rc-comparison-card--stretch': verticalStretch,
          'rc-comparison-card--clickable': !disableCardClick || !linkElement,
          'rc-comparison-card--expedited': expeditedShipping,
        },
      )}
    >
      <PassedLink className="rc-comparison-card__link">
        <img
          className="rc-comparison-card__thumbnail"
          src={thumbnailURL}
          alt={thumbnailAlt}
        />
      </PassedLink>
      <div className="rc-comparison-card__body">

        <div className="rc-comparison-card__titleblock">
          <PassedLink
            className="rc-comparison-card__link rc-comparison-card__link--title"
            title={title}
          >
            <TitleElement className="rc-comparison-card__title">
              {title}
            </TitleElement>
          </PassedLink>
          <span className="rc-comparison-card__condition">
            {condition}
          </span>
        </div>

        <div className="rc-comparison-card__price-block">
          <span className="visually-hidden">
            {a11yPriceDescription}
          </span>
          {originalPrice &&
            <div className="rc-comparison-card__discount-block" aria-hidden>
              <span className="rc-comparison-card__original-price">
                {originalPrice}
              </span>
              {discountText &&
                <span className="rc-comparison-card__discount-text">
                  {discountText}
                </span>
              }
            </div>
          }
          <span
            className="rc-comparison-card__price"
            aria-hidden
          >
            {price}
          </span>
          <span className="rc-comparison-card__shipping-price">
            {expeditedShipping &&
              <ExpeditedShippingTruckIcon />
            }
            {shippingPrice}
          </span>
        </div>

        <div className="rc-comparison-card__shop">
          <div className="rc-comparison-card__shop-titleblock">
            <span className="rc-comparison-card__shop-name">
              {shopName}
            </span>
            {(shopRating > 0) &&
              <div className="rc-comparison-card__shop-rating">
                <RCStarRating
                  rating={shopRating}
                  hideTooltip
                />
              </div>
            }
          </div>
          <span className="rc-comparison-card__shop-location">
            {shopLocation}
          </span>
        </div>

        {(signals?.length > 0) &&
          <div className="rc-comparison-card__signals">
            {signals.map(signal => (
              <div
                key={signal.name}
                className="rc-comparison-card__signal"
              >
                <img
                  className="rc-comparison-card__signal__icon"
                  src={signal.icon}
                  alt=""
                />
                <span className="rc-comparison-card__signal__title">
                  {signal.title}
                </span>
              </div>
            ))}
          </div>
        }

        {(primaryAction || secondaryAction) &&
          <div className="rc-comparison-card__actions">
            {secondaryAction &&
              <div className="rc-comparison-card__action rc-comparison-card__action--secondary">
                {secondaryAction}
              </div>
            }
            {primaryAction &&
              <div className="rc-comparison-card__action rc-comparison-card__action--primary">
                {primaryAction}
              </div>
            }
          </div>
        }

        {watchButtonElement &&
          <div className="rc-comparison-card__watch-container">
            {/*
              if watchButtonElement is present, load that and skip isWatched/onWatchClick,
              else pass those values into RCWatchButton when that work rolls around
            */}
            {React.cloneElement(
              watchButtonElement,
              {
                onClick: onWatchClick,
                isWatched: isWatched,
              },
            )}
          </div>
        }
      </div>
    </div>
  );
}
